/* Build in libraries */

import { useState } from 'react';
import { useLocation } from 'react-router';
import CityFundsRequestedWidget from '@/components/Rover/Widgets/CityFundsRequestedWidget';
import DeveloperInvestmentsProposedWidget from '@/components/Rover/Widgets/DeveloperInvestmentsProposedWidget';
import MinorityDevelopersWidget from '@/components/Rover/Widgets/MinorityDevelopersWidget';
import TotalAffdForSaleUnitsProposedWidget from '@/components/Rover/Widgets/TotalAffdForSaleUnitsProposedWidget';
import TotalAffdRentalUnitsProposedWidget from '@/components/Rover/Widgets/TotalAffdRentalUnitsProposedWidget';
/* Components */
import TotalCoBLandPurchasesWidget from '@/components/Rover/Widgets/TotalCoBLandPurchasesWidget';
import TotalForSaleUnitsProposedWidget from '@/components/Rover/Widgets/TotalForSaleUnitsProposedWidget';
import TotalMarketRentalUnitsProposedWidget from '@/components/Rover/Widgets/TotalMarketRentalUnitsProposedWidget';
import TotalProjectsProposedWidget from '@/components/Rover/Widgets/TotalProjectsProposedWidget';
import TotalUnitsProposedWidget from '@/components/Rover/Widgets/TotalUnitsProposedWidget';
import ValueOfProposedProjectsWidget from '@/components/Rover/Widgets/ValueOfProposedProjectsWidget';
import Card from '@/components/UI/Card/index.tsx';
import { EApplicationStatus } from '@/features/ApplicationForm/constants';
/* Services */
import { convertEnumToText, isObjectEmpty } from '@/utils';
import { BakerTillyApprovedDashboard } from './BDCDPApprovedDashboard';

interface IApplicationDashboardComponentProps {
  data: any;
}

export const BakerTillyDashboard = ({ data }: IApplicationDashboardComponentProps) => {
  const _location = useLocation();
  const [_selectedProject, _setSelectedProject] = useState<any>();
  const [viewStatus, setViewStatus] = useState('ALL');

  const applicationsList = data?.data;

  const widgetsData: {
    statusSummary: { [key: string]: number };
    totalProjectProposed: number;
    valueOfProposedProjects: number;
    totalCoBLandPurchases: number;
    totalUnitsProposed: number;
    cityFundsRequested: number;
    totalForSaleUnitsProposed: number;
    totalAffdForSaleUnitsProposed: number;
    totalAffdRentalUnitsProposed: number;
    totalMarketRentalUnitsProposed: number;
    minorityDevelopers: number;
    developerInvestmentsProposed: number;
    applicationLocationList: Array<{
      id: string;
      [key: string]: string;
      fullAddress: string;
    }>;
  } = {
    statusSummary: {},
    totalProjectProposed: 0,
    valueOfProposedProjects: 0,
    totalCoBLandPurchases: 0,
    totalUnitsProposed: 0,
    cityFundsRequested: 0,
    totalForSaleUnitsProposed: 0,
    totalAffdForSaleUnitsProposed: 0,
    totalAffdRentalUnitsProposed: 0,
    totalMarketRentalUnitsProposed: 0,
    minorityDevelopers: 0,
    developerInvestmentsProposed: 0,
    applicationLocationList: [],
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData, id } = a;

    // Location
    const applicationLocation = `${applicationData?.projectDetails?.streetAddress}, ${applicationData?.projectDetails?.city}, ${applicationData?.projectDetails?.state}, ${applicationData?.projectDetails?.zipCode}`;
    widgetsData.applicationLocationList.push({
      id: id,
      email: applicationData?.applicantInfo?.contactPersonEmail,
      name: applicationData?.applicantInfo?.entityName,
      phone: applicationData?.applicantInfo?.phone,
      fullAddress: applicationLocation || '',
    });

    //Status Summary
    widgetsData.statusSummary[a.status] = (widgetsData.statusSummary[a.status] ?? 0) + 1;

    // Total Project Proposed
    widgetsData.totalProjectProposed += 1;

    // Value of Proposed Projects
    widgetsData.valueOfProposedProjects += applicationData?.projectDetails?.developmentCostEstimate;

    applicationData?.projectFinance?.sources?.forEach((s: any) => {
      //City Funds Requested
      if (s?.name === 'City of Birmingham') {
        widgetsData.cityFundsRequested += s?.amount;
      }

      // Developer Investments Proposed
      if (s?.name === 'Developer Equity') {
        widgetsData.developerInvestmentsProposed += s?.amount;
      }
    });

    if (applicationData?.leadershipInformation?.hasMajorityOwnership === 'yes') {
      widgetsData.minorityDevelopers += 1;
    }

    // Total Units Proposed
    widgetsData.totalUnitsProposed += applicationData?.projectDetails?.proposedUnits ?? 0;

    // Total CoB Land Purchases
    if (applicationData?.projectDetails?.isPropertyCityOwn === 'yes') {
      widgetsData.totalCoBLandPurchases += applicationData?.projectDetails?.proposedAmount;
    }

    // Total Aff. For-Sale Units Proposed
    if (applicationData?.projectDetails?.projectType === 'Affordable Homeownership') {
      widgetsData.totalAffdForSaleUnitsProposed += applicationData?.projectDetails?.proposedUnits;
    }

    // Total Aff. Rental Units Proposed
    if (applicationData?.projectDetails?.projectType === 'Affordable Rental Housing: 4%, 9%') {
      widgetsData.totalAffdRentalUnitsProposed += applicationData?.projectDetails?.proposedUnits;
    }

    // Total For-Sale Units Proposed
    if (applicationData?.projectDetails?.projectType === 'Home Ownership') {
      widgetsData.totalForSaleUnitsProposed += applicationData?.projectDetails?.proposedUnits;
    }

    // Total Market Rental Units Proposed
    if (applicationData?.projectDetails?.projectType === 'Market Rate Multifamily') {
      widgetsData.totalMarketRentalUnitsProposed += applicationData?.projectDetails?.proposedUnits;
    }
  });

  // const fetchApplicationLocation = useQueries({
  //   queries: widgetsData?.applicationLocationList?.map(
  //     (d: { id: string; [key: string]: string; fullAddress: string }) => {
  //       return {
  //         queryKey: ['GET_APPLICATION_WIDGET_GEO_CODE', d?.id],
  //         queryFn: () => getAddressDecode(d.fullAddress),
  //         select: (resData: { data: IGetAddressDecodeResData }) => {
  //           const { lat, lng } = resData.data;
  //           return { ...d, coordinates: { lat, lng } };
  //         },
  //       };
  //     }
  //   ),
  // });
  // const applicationsGeoLocated = fetchApplicationLocation?.map((d) => d.data);
  return (
    <div className='max-w-screen-3xl mx-auto'>
      <div className='font-semibold text-bold py-4 rounded-md space-x-2'>
        <span
          className={`${
            viewStatus === 'ALL' ? 'bg-primaryColor-normal' : 'bg-neutralColor-light hover:bg-primaryColor-normal'
          } text-white px-4 py-2 cursor-pointer`}
          onClick={() => setViewStatus('ALL')}
        >
          All Submissions
        </span>
        <span
          className={`${
            viewStatus === 'APPROVED' ? 'bg-primaryColor-normal' : 'bg-neutralColor-light hover:bg-primaryColor-normal'
          } text-white px-4 py-2 cursor-pointer`}
          onClick={() => setViewStatus('APPROVED')}
        >
          Approved Submissions
        </span>
      </div>
      {viewStatus === 'ALL' && (
        <>
          {/* <section className='my-4'>
              <Card title='Project Location'>
                {applicationsGeoLocated.length === 0 && (
                  <div className='w-full flex justify-center items-center h-screen/2'>
                    <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                      <MapPinIcon className='h-8 w-8' />
                      <h1>No Projects</h1>
                    </div>
                    <p>There are no assigned projects to display.</p>
                  </div>
                )}
                {applicationsGeoLocated.length > 0 && (
                  <div className='w-full grid grid-cols-6 h-screen/2'>
                    <div className='col-span-6 w-full border-l p-5'>
                      {applicationsGeoLocated[0] && (
                        <GoogleMap
                          defaultCenter={{
                            lat: applicationsGeoLocated[0].coordinates.lat,
                            lng: applicationsGeoLocated[0].coordinates.lng,
                          }}
                        >
                          {applicationsGeoLocated?.map((res: any) => {
                            return (
                              <AdvancedMarker
                                key={res?.id}
                                position={{
                                  lat: res?.coordinates?.lat,
                                  lng: res?.coordinates?.lng,
                                }}
                              >
                                <div className='relative'>
                                  {selectedProject &&
                                    selectedProject?.id === res?.id && (
                                      <div
                                        onClick={() =>
                                          setSelectedProject(undefined)
                                        }
                                        className='bg-white border rounded absolute p-3 bottom-4 left-5 w-56'
                                      >
                                        <XCircleIcon className='h-4 w-4 absolute right-1 top-1 text-neutralColor-normal hover:text-red-500' />
                                        <Link
                                          to={`${location.pathname.replace(
                                            'dashboard',
                                            'applications'
                                          )}/${
                                            selectedProject?.id
                                          }/application_details`}
                                        >
                                          <p className='font-semibold text-primaryColor-normal underline'>
                                            {selectedProject?.name}
                                          </p>
                                        </Link>
                                        <p className='text-xs'>
                                          {selectedProject?.fullAddress}
                                        </p>
                                        <div className='mt-2 text-sm flex justify-between gap-2'>
                                          <span>Email</span>
                                          <span className='font-semibold'>
                                            {selectedProject?.email}
                                          </span>
                                        </div>
                                        <div className='mt-2 text-sm flex justify-between gap-2'>
                                          <span>Phone</span>
                                          <span className='font-semibold'>
                                            <PhoneNumberFormatDisplay
                                              value={
                                                selectedProject?.phone || 'none'
                                              }
                                            />
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  {res && (
                                    <MapPinIcon
                                      onClick={() => setSelectedProject(res)}
                                      className={
                                        selectedProject?.id === res?.id
                                          ? 'h-8 w-8 text-black'
                                          : 'h-8 w-8 text-red-500 '
                                      }
                                    />
                                  )}
                                </div>
                              </AdvancedMarker>
                            );
                          })}
                        </GoogleMap>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            </section> */}
          <section className='my-4'>
            <Card title='Status Summary'>
              <div className='grid grid-cols-8 gap-6 py-6 px-6'>
                {!isObjectEmpty(EApplicationStatus) &&
                  Object.keys(EApplicationStatus).map((s: any) => (
                    <div key={s} className='col-span-2 font-medium flex justify-between gap-6'>
                      <span>{convertEnumToText(EApplicationStatus[s as keyof typeof EApplicationStatus])}</span>
                      <span>{widgetsData.statusSummary[s] ?? 0}</span>
                    </div>
                  ))}
              </div>
            </Card>
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
            <TotalProjectsProposedWidget count={widgetsData?.totalProjectProposed ?? 0} />
            <ValueOfProposedProjectsWidget count={widgetsData?.valueOfProposedProjects ?? 0} />
            <TotalCoBLandPurchasesWidget count={widgetsData?.totalCoBLandPurchases ?? 0} />
            <MinorityDevelopersWidget count={widgetsData?.totalProjectProposed ?? 0} />
            <TotalUnitsProposedWidget count={widgetsData?.totalUnitsProposed ?? 0} />
            <TotalForSaleUnitsProposedWidget count={widgetsData?.totalForSaleUnitsProposed ?? 0} />
            <TotalAffdForSaleUnitsProposedWidget count={widgetsData?.totalAffdForSaleUnitsProposed ?? 0} />
            <TotalAffdRentalUnitsProposedWidget count={widgetsData?.totalAffdRentalUnitsProposed ?? 0} />
            <TotalMarketRentalUnitsProposedWidget count={widgetsData?.totalMarketRentalUnitsProposed ?? 0} />
            <CityFundsRequestedWidget count={widgetsData?.cityFundsRequested ?? 0} />
            <DeveloperInvestmentsProposedWidget count={widgetsData?.developerInvestmentsProposed ?? 0} />
          </section>
        </>
      )}
      {viewStatus === 'APPROVED' && <BakerTillyApprovedDashboard data={data} />}
    </div>
  );
};
